import React, { useEffect, useRef, useState } from 'react'
import type { User } from 'firebase/auth'
import styles from './CrewWebPage.module.css'
import { CREW_PLAY_URL, crewStoreFor } from '../lib/crewApp'
import { crewSetName, crewSignInRedirect, crewSignOut, enabledProviders, onCrewAuthStateChange, type ProviderId } from '../lib/firebase'
import {
  type ClockOnQr,
  type CrewWebStore,
  TAG_VALID_MS,
  encodeTag,
  loadStore,
  netTicks,
  normaliseCode,
  qrFromSearch,
  qrRows,
  readClockOnQr,
  saveStore,
  shiftEndsAfter,
  usableShift,
  sendClockOffs,
  sendDeferred,
  withClockOff,
  withDeferred,
} from '../lib/crewWeb'

// /crew: Dataphyll Crew in Safari, for iPhones until the App Store listing is
// live (lib/crewWeb.ts). Tagless harvest only, plus an induction code so the
// induction email works on an iPhone too. Every other team is told to wait for
// the app. A phone camera's scan of a clock-on QR arrives here from /crewapp
// (index.html) with the QR in the query.

const PROVIDER_LABEL: Record<ProviderId, string> = { apple: 'Apple', google: 'Google', microsoft: 'Microsoft', meta: 'Facebook' }
const PROVIDER_ORDER: ProviderId[] = ['apple', 'google', 'microsoft']

const NOT_TAGLESS = 'This team needs the Dataphyll Crew app, which is coming to the App Store soon. On iPhone, only Tagless harvest teams can clock on here for now.'

type View =
  | { kind: 'home' }
  | { kind: 'code' }
  | { kind: 'inducted'; place: string }
  | { kind: 'tag' }

// A first and a last name: the platform makes nobody without both.
const hasFullName = (name: string | null | undefined) => (name || '').trim().split(/\s+/).filter(Boolean).length >= 2

const isOffline = (e: unknown) =>
  e instanceof TypeError || !!(e as { offline?: boolean })?.offline || (typeof navigator !== 'undefined' && navigator.onLine === false)

async function crewFetch<T>(user: User, path: string, init?: RequestInit): Promise<T> {
  const token = await user.getIdToken()
  const res = await fetch(path, { ...init, headers: { ...(init?.headers || {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } })
  const body = await res.json().catch(() => null)
  if (!res.ok) throw Object.assign(new Error(body?.error || 'Something went wrong. Try again.'), { status: res.status, code: body?.code })
  // An answer that is not Control's (a Wi-Fi sign-in page, say) is no signal.
  if (!body || typeof body !== 'object') throw Object.assign(new Error('No signal'), { offline: true })
  return body as T
}

// The page and its files kept on the phone, so it opens with no signal
// (public/crew-sw.js). The files this visit loaded are handed over, since they
// came before the worker was there to keep them.
function registerOffline() {
  if (!('serviceWorker' in navigator) || import.meta.env.MODE === 'test') return
  navigator.serviceWorker
    .register('/crew-sw.js', { scope: '/crew' })
    .then(() => navigator.serviceWorker.ready)
    .then((reg) => {
      const urls = performance
        .getEntriesByType('resource')
        .map((e) => e.name)
        .filter((u) => u.startsWith(location.origin) && !u.includes('/api/') && !u.includes('/__/'))
      reg.active?.postMessage({ keep: ['/crew', ...urls] })
    })
    .catch(() => {})
}

export function CrewWebPage() {
  const iphone = crewStoreFor(navigator.userAgent, navigator.maxTouchPoints) === 'ios'
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [store, setStore] = useState<CrewWebStore | null>(null)
  const [view, setView] = useState<View>({ kind: 'home' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The sign-in's name, asked for when it has no first and last name.
  const [name, setName] = useState('')
  // A camera scan's QR, waiting for the sign-in.
  const pendingQr = useRef<string | null>(qrFromSearch(window.location.search))

  useEffect(() => {
    registerOffline()
    // Firebase can sit on an unfinished sign-in (a cancelled Apple sheet, say)
    // and never say who is signed in. The buttons come up after a few
    // seconds regardless; a sign-in that does arrive replaces them.
    const wait = setTimeout(() => setUser((u) => (u === undefined ? null : u)), 4000)
    let stop = () => {}
    try {
      stop = onCrewAuthStateChange((u) => {
        clearTimeout(wait)
        setUser(u)
        setName(u?.displayName?.trim() || '')
        setStore(u ? loadStore(u.uid) : null)
      })
    } catch {
      setUser(null)
    }
    return () => {
      clearTimeout(wait)
      stop()
    }
  }, [])

  const update = (fn: (s: CrewWebStore) => CrewWebStore) =>
    setStore((s) => {
      if (!s) return s
      const next = fn(s)
      saveStore(next)
      return next
    })

  // A shift past its team's clock-off is over.
  const shift = usableShift(store?.shift) && store.shift.endsAtMs > Date.now() ? store.shift : null

  const clockOn = async (url: string) => {
    if (!user || !store) return
    setError(null)
    const qr = readClockOnQr(url)
    if (!qr) return setError('That is not a Clock On QR')
    const induction = qr.settings.induct === '1'
    if (!induction && qr.settings.mode !== 'tagless') return setError(NOT_TAGLESS)
    setBusy(true)
    const at = Date.now()
    try {
      const res = await crewFetch<{ human_id: string; tenant_id: string; team_id: string }>(user, '/api/crew/clock-on', {
        method: 'POST',
        // The tag's shift start, so Control keeps this clock-on and the
        // receipter's scan of the tag as one (crew-clock.js ingestWebClockOn).
        body: JSON.stringify({ qr: url, scanned_at: Math.floor(at / 1000), device_id: 'crew-web', shift_ticks: netTicks(new Date(at)) }),
      })
      if (typeof res.human_id !== 'string' || !res.human_id) throw Object.assign(new Error('No signal'), { offline: true })
      started(qr, res.human_id, at, induction)
    } catch (e) {
      // No signal: a worker this phone has clocked on before in this
      // workspace starts anyway, and the clock-on is sent once there is
      // signal (sendDeferred), or reaches Control sooner on the receipter's
      // scan of their tag. A first clock-on needs the server to make them.
      const known = store.humans[qr.tenantId]
      if (isOffline(e) && known && !induction && qr.expires * 1000 > at) {
        started(qr, known, at, false)
        update((s) => withDeferred(s, { qr: url, at }))
      }
      else if ((e as { code?: string }).code === 'name_required') {
        pendingQr.current = url
        setName('')
      } else setError(isOffline(e) ? 'No signal. Your first clock-on here needs signal; try again where you have it.' : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const started = (qr: ClockOnQr, humanId: string, at: number, induction: boolean) => {
    update((s) => ({
      ...s,
      humans: { ...s.humans, [qr.tenantId]: humanId },
      shift: induction
        ? s.shift
        : {
            tenantId: qr.tenantId,
            teamId: qr.teamId,
            teamName: qr.teamName,
            siteName: qr.siteName,
            humanId,
            name,
            shiftStartMs: at,
            endsAtMs: shiftEndsAfter(at, qr.settings.clockoff),
          },
    }))
    setView(induction ? { kind: 'inducted', place: qr.siteName || qr.teamName } : { kind: 'home' })
  }

  // Clock-ons made with no signal, then Clock off presses, go to Control when
  // the page opens, when the phone is back online or the page back in view,
  // and each minute while any wait. Clock-ons first, so a clock-off's
  // clock-on is there before it. What was sent is taken off the list as it
  // stands then, so one added meanwhile stays.
  const sending = useRef(false)
  const hasDeferred = !!store?.deferred.length || !!store?.clockOffs.length
  useEffect(() => {
    if (!user || !hasDeferred) return
    const send = async () => {
      if (sending.current) return
      sending.current = true
      try {
        const list = loadStore(user.uid).deferred
        const left = await sendDeferred(list, (d) =>
          crewFetch(user, '/api/crew/clock-on', {
            method: 'POST',
            body: JSON.stringify({ qr: d.qr, scanned_at: Math.floor(d.at / 1000), deferred: true, device_id: 'crew-web', shift_ticks: netTicks(new Date(d.at)) }),
          })
        )
        const done = list.filter((d) => !left.includes(d)).map((d) => `${d.at}|${d.qr}`)
        if (done.length) update((s) => ({ ...s, deferred: s.deferred.filter((d) => !done.includes(`${d.at}|${d.qr}`)) }))
        const offs = loadStore(user.uid).clockOffs
        const offsLeft = await sendClockOffs(offs, (c) =>
          crewFetch(user, '/api/crew/web-clock-off', {
            method: 'POST',
            body: JSON.stringify({ tenant_id: c.tenantId, team_id: c.teamId, shift_ticks: c.shiftTicks, at: c.at }),
          })
        )
        const key = (c: { at: number; teamId: string; shiftTicks: string }) => `${c.at}|${c.teamId}|${c.shiftTicks}`
        const sent = offs.filter((c) => !offsLeft.includes(c)).map(key)
        if (sent.length) update((s) => ({ ...s, clockOffs: s.clockOffs.filter((c) => !sent.includes(key(c))) }))
      } finally {
        sending.current = false
      }
    }
    void send()
    const id = window.setInterval(send, 60_000)
    // iOS does not always say it is back online; it does say the page is back.
    const shown = () => document.visibilityState === 'visible' && void send()
    window.addEventListener('online', send)
    document.addEventListener('visibilitychange', shown)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('online', send)
      document.removeEventListener('visibilitychange', shown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasDeferred])

  // A camera scan clocks on as soon as the worker is signed in and named.
  useEffect(() => {
    if (!user || !store || !hasFullName(name) || !pendingQr.current) return
    const url = pendingQr.current
    pendingQr.current = null
    window.history.replaceState(null, '', '/crew')
    void clockOn(url)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, store, name])

  const saveName = async (first: string, last: string) => {
    if (!user) return
    const full = `${first.trim().replace(/\s+/g, ' ')} ${last.trim().replace(/\s+/g, ' ')}`
    setError(null)
    setBusy(true)
    try {
      await crewSetName(user, full)
      setName(full)
    } catch (e) {
      setError(isOffline(e) ? 'No signal. Your name needs signal to save; try again where you have it.' : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const typedCode = async (raw: string) => {
    if (!user || !store) return
    const code = normaliseCode(raw)
    if (!code) return
    setError(null)
    let url: string | undefined = store.codes[code]
    if (!url) {
      setBusy(true)
      try {
        const res = await crewFetch<{ teams?: { code: string; qr: string }[] }>(user, `/api/crew/teams?qrv=2&code=${encodeURIComponent(code)}`)
        const codes = Object.fromEntries((res.teams || []).map((t) => [normaliseCode(t.code), t.qr]))
        update((s) => ({ ...s, codes }))
        url = codes[code]
      } catch (e) {
        setBusy(false)
        return setError(isOffline(e) ? 'No signal. A new code needs signal; try again where you have it.' : (e as Error).message)
      }
      setBusy(false)
    }
    if (!url) return setError('No team has that code. Check it with your supervisor.')
    await clockOn(url)
  }

  if (!iphone) {
    return (
      <Shell>
        <h1 className={styles.title}>Dataphyll Crew</h1>
        <p className={styles.lead}>This page is for iPhone. On Android, install the Dataphyll Crew app.</p>
        <a className={styles.primary} href={CREW_PLAY_URL}>
          Get it on Google Play
        </a>
      </Shell>
    )
  }

  if (user === undefined) {
    return (
      <Shell>
        <p className={styles.lead}>Loading…</p>
      </Shell>
    )
  }

  if (!user) {
    return (
      <Shell>
        <h1 className={styles.title}>Dataphyll Crew</h1>
        <p className={styles.lead}>Sign in to clock on and show your tag.</p>
        <div className={styles.stack}>
          {PROVIDER_ORDER.filter((p) => enabledProviders.includes(p)).map((p) => (
            <button
              key={p}
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={async () => {
                setError(null)
                setBusy(true)
                // A sign-in that goes ahead leaves the page; one still here
                // after a while is stuck, so say so rather than sit greyed out.
                const stuck = setTimeout(() => {
                  setBusy(false)
                  setError('Sign-in did not start. Close this tab, open the link again in Safari, and try once more.')
                }, 10000)
                try {
                  await crewSignInRedirect(p)
                } catch (e) {
                  clearTimeout(stuck)
                  setBusy(false)
                  setError((e as Error).message)
                }
              }}
            >
              Continue with {PROVIDER_LABEL[p]}
            </button>
          ))}
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
      </Shell>
    )
  }

  if (!hasFullName(name)) {
    return (
      <Shell>
        <h1 className={styles.title}>Your name</h1>
        <p className={styles.lead}>Enter your legal first and last name. Orchards use this to process your pay, so make sure it’s correct.</p>
        <form
          className={styles.stack}
          onSubmit={(e) => {
            e.preventDefault()
            const f = new FormData(e.currentTarget)
            const first = String(f.get('first') || '').trim()
            const last = String(f.get('last') || '').trim()
            if (!first || !last) return setError('Enter both your first and last name.')
            void saveName(first, last)
          }}
        >
          <input className={styles.field} name="first" autoComplete="given-name" autoCapitalize="words" aria-label="First name" placeholder="First name" autoFocus />
          <input className={styles.field} name="last" autoComplete="family-name" autoCapitalize="words" aria-label="Last name" placeholder="Last name" />
          <button type="submit" className={styles.primary} disabled={busy}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </form>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <p className={styles.foot}>
          <button type="button" className={styles.link} onClick={() => void crewSignOut()}>
            Sign out
          </button>
        </p>
      </Shell>
    )
  }

  if (view.kind === 'tag' && shift) return <TagView shift={shift} onDone={() => setView({ kind: 'home' })} />

  if (view.kind === 'inducted') {
    return (
      <Shell>
        <h1 className={styles.title}>Great, you’re inducted into {view.place}</h1>
        <p className={styles.lead}>This does not clock you on. On your first day, scan your team’s Clock On QR with the iPhone camera.</p>
        <button type="button" className={styles.primary} onClick={() => setView({ kind: 'home' })}>
          Done
        </button>
      </Shell>
    )
  }

  if (view.kind === 'code') {
    return (
      <Shell>
        <h1 className={styles.title}>Enter access code</h1>
        <form
          className={styles.stack}
          onSubmit={(e) => {
            e.preventDefault()
            void typedCode(String(new FormData(e.currentTarget).get('code') || ''))
          }}
        >
          <input className={styles.code} name="code" autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false} aria-label="Access code" autoFocus />
          <button type="submit" className={styles.primary} disabled={busy}>
            {busy ? 'Checking…' : 'Clock on'}
          </button>
          <button type="button" className={styles.ghost} onClick={() => (setError(null), setView({ kind: 'home' }))}>
            Back
          </button>
        </form>
        {error && <p className={styles.error} role="alert">{error}</p>}
      </Shell>
    )
  }

  return (
    <Shell>
      {shift ? (
        <>
          <p className={styles.kicker}>Clocked on</p>
          <h1 className={styles.title}>{shift.teamName}</h1>
          <p className={styles.lead}>{shift.siteName}</p>
          <button type="button" className={styles.tagButton} onClick={() => setView({ kind: 'tag' })}>
            Show my tag
          </button>
          <button type="button" className={styles.ghost} onClick={() => update((s) => withClockOff(s))}>
            Clock off
          </button>
        </>
      ) : (
        <>
          <h1 className={styles.title}>Clock on</h1>
          <p className={styles.lead}>Scan your team’s Clock On QR with the iPhone camera, or type its code.</p>
          <button type="button" className={styles.primary} disabled={busy} onClick={() => (setError(null), setView({ kind: 'code' }))}>
            Enter code manually
          </button>
        </>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
      <p className={styles.foot}>
        Signed in as {name} ·{' '}
        <button type="button" className={styles.link} onClick={() => void crewSignOut()}>
          Sign out
        </button>
      </p>
    </Shell>
  )
}

function Shell({ children }: { children?: React.ReactNode }) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>{children}</div>
    </div>
  )
}

// One showing is one code, drawn once, closed when it expires: the receipter
// tells a second scan of the same handover from a new one by the code alone
// (worker_tag.dart).
function TagView({ shift, onDone }: { shift: NonNullable<CrewWebStore['shift']>; onDone: () => void }) {
  const [shown] = useState(() => {
    const at = Date.now()
    return { at, rows: qrRows(encodeTag(shift, new Date(at))) }
  })
  const [left, setLeft] = useState(TAG_VALID_MS / 1000)
  const done = useRef(onDone)
  done.current = onDone

  useEffect(() => {
    const t = setInterval(() => {
      const s = Math.ceil((shown.at + TAG_VALID_MS - Date.now()) / 1000)
      if (s <= 0) done.current()
      else setLeft(s)
    }, 250)
    // Keep the screen on while the receipter scans, where Safari allows it.
    let lock: { release: () => Promise<void> } | null = null
    ;(navigator as any).wakeLock?.request('screen').then((l: typeof lock) => (lock = l)).catch(() => {})
    return () => {
      clearInterval(t)
      void lock?.release().catch(() => {})
    }
  }, [shown])

  const n = shown.rows.length
  return (
    <div className={styles.tagPage}>
      <p className={styles.tagName}>{shift.name || 'My tag'}</p>
      <p className={styles.tagTeam}>{shift.teamName}</p>
      <svg className={styles.tagQr} viewBox={`-4 -4 ${n + 8} ${n + 8}`} role="img" aria-label="My tag QR" shapeRendering="crispEdges">
        <rect x={-4} y={-4} width={n + 8} height={n + 8} fill="#fff" />
        {shown.rows.map((row, r) =>
          [...row.matchAll(/1+/g)].map((m) => <rect key={`${r}-${m.index}`} x={m.index} y={r} width={m[0].length} height={1} fill="#000" />),
        )}
      </svg>
      <p className={styles.tagLeft}>Turn your brightness up · closes in {left}s</p>
      <button type="button" className={styles.ghost} onClick={onDone}>
        Done
      </button>
    </div>
  )
}
